import React, { useMemo, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Search, Upload, CheckCircle2, ShieldCheck, Clock, AlertCircle, FileText, X, Download, Users, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { custodianAPI, allocationRequestsAPI, holdingsAPI, usersAPI, formatDate, formatCurrency } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const statusBadgeConfig = {
    executing: { label: 'Executing', color: 'bg-purple-100 text-purple-800', icon: Clock },
    completed: { label: 'Completed', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
    failed: { label: 'Failed', color: 'bg-red-100 text-red-800', icon: AlertCircle },
    pending: { label: 'Pending', color: 'bg-gray-100 text-gray-700', icon: Clock },
    processing: { label: 'Processing', color: 'bg-purple-100 text-purple-800', icon: Clock },
    staged: { label: 'Pending L1', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
    l1_approved: { label: 'Pending L2', color: 'bg-orange-100 text-orange-800', icon: Clock }
};

const renderStatusBadge = (distribution) => {
    if (!distribution) {
        return (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                <Clock className="w-3.5 h-3.5 mr-1" />
                No Distribution
            </span>
        );
    }
    const { status } = distribution;
    const config = statusBadgeConfig[status] || statusBadgeConfig.pending;
    const Icon = config.icon;
    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
            <Icon className="w-3.5 h-3.5 mr-1" />
            {config.label}
        </span>
    );
};

const formatToken = (value) => new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4
}).format(Number(value || 0));

function BondTokenAllocation() {
    const queryClient = useQueryClient();
    const { user } = useAuth();
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [uploadContext, setUploadContext] = useState(null);
    const [uploadFile, setUploadFile] = useState(null);
    const [detailsContext, setDetailsContext] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [csvErrors, setCsvErrors] = useState([]);
    const [csvWarnings, setCsvWarnings] = useState([]);
    const [csvStats, setCsvStats] = useState(null);
    const [parsedData, setParsedData] = useState([]);
    const [showPreview, setShowPreview] = useState(false);
    const [manualAllocations, setManualAllocations] = useState({});
    const [editingInvestor, setEditingInvestor] = useState(null); // Format: 'bondId-investorId'
    const [approvedAllocations, setApprovedAllocations] = useState({}); // Track approved allocations by bondId-investorId
    const [confirmationModal, setConfirmationModal] = useState(null); // { type: 'l1' | 'l2', bondId, message }
    const [manualApprovalStates, setManualApprovalStates] = useState({}); // Track L1/L2 approval states for manual allocations by bondId

    // Fetch parent account if user doesn't have maybankMasterAccountNo
    const { data: parentAccount } = useQuery({
        queryKey: ['parent-account', user?.parentAccountId],
        queryFn: async () => {
            if (!user?.parentAccountId) return null;
            const response = await usersAPI.getUser(user.parentAccountId);
            return response?.data?.data?.user || response?.data?.user || response?.data?.data || response?.data || null;
        },
        enabled: !!user?.parentAccountId && !user?.maybankMasterAccountNo,
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    });

    // Get effective master account number (from user or parent)
    const effectiveMasterAccountNo = user?.maybankMasterAccountNo || parentAccount?.maybankMasterAccountNo || null;
    const effectiveMasterAccountName = user?.maybankMasterAccountName || parentAccount?.maybankMasterAccountName || null;
    const hasMasterAccountConfigured = !!effectiveMasterAccountNo;

    const downloadTemplate = () => {
        // Use actual custodian master account if available, otherwise use placeholder
        const masterAccount = effectiveMasterAccountNo || '1234567890';
        const masterAccountName = effectiveMasterAccountName || 'Maybank Custodian Account';

        const csvContent = [
            'MASTER ACCOUNT,MASTER ACCOUNT NAME,ACCOUNT,ACCOUNT NAME,SECURITY CODE,SECURITY NAME,SECURITY LOCATION,CURRENCY,TRADE DATE,SETTLEMENT DATE,SETTLEMENT AMOUNT,QTY,UNIQUE_REF_NUM,REFNO,COUNTERPARTY,COUNTERPARTY NAME,CASH_ACCOUNT,MAKER,MAKERDT,CHECKER,CHECKERDT,NON_BILLABLE,SCRIPTLESS FLAG,TRADESTATUS,WALLET NO',
            `${masterAccount},${masterAccountName},ACC001,Sub-Investor Account 1,MY0000012345,Bond Name,Malaysia,MYR,2024-01-15,2024-01-17,10000000,10000000,REF001,REF001,COUNTERPARTY1,Counterparty Name 1,CASH001,John Maker,2024-01-15 10:00:00,Jane Checker,2024-01-15 14:00:00,N,Y,COMPLETED,WALLET001`,
            `${masterAccount},${masterAccountName},ACC002,Sub-Investor Account 2,MY0000012345,Bond Name,Malaysia,MYR,2024-01-15,2024-01-17,15000000,15000000,REF002,REF002,COUNTERPARTY2,Counterparty Name 2,CASH002,John Maker,2024-01-15 10:30:00,Jane Checker,2024-01-15 14:30:00,N,Y,COMPLETED,WALLET002`,
            '',
            '# Maybank CSV Format Template for Bond Token Allocation',
            '# Required fields (must be present):',
            `# - MASTER ACCOUNT: Custodian Maybank account number (must match your account${effectiveMasterAccountNo ? ': ' + effectiveMasterAccountNo : ''})`,
            '# - MASTER ACCOUNT NAME: Custodian account name',
            '# - ACCOUNT: Sub-investor Maybank custody account number',
            '# - ACCOUNT NAME: Sub-investor account name',
            '# - SECURITY CODE: Bond ISIN (must match the selected bond)',
            '# - QTY: Bond amount to allocate',
            '',
            '# Optional fields for L1/L2 approval bypass:',
            '# - MAKER: Name of the person who created the allocation',
            '# - MAKERDT: Date and time when allocation was created',
            '# - CHECKER: Name of the person who verified the allocation',
            '# - CHECKERDT: Date and time when allocation was verified',
            '# Note: If MAKER, MAKERDT, CHECKER, and CHECKERDT are ALL provided, L1/L2 approval will be skipped',
            '',
            '# Important notes:',
            '# - All rows must be valid or the entire upload will be rejected',
            '# - ACCOUNT must match a sub-investor created under your custodian account',
            '# - SECURITY CODE (ISIN) must match the bond you are allocating',
            '# - Total QTY cannot exceed your custodian held tokens for this bond',
            '# - Sub-investors must have active wallets'
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'maybank_bond_token_allocation_template.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const parseCSV = (csvText, custodianHeldTokens = 0, bondIsin = null, custodianMasterAccountNo = null) => {
        const lines = csvText.trim().split('\n');
        const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));

        // Detect CSV format (Maybank vs Simple)
        const normalizedHeaders = headers.map(h => h.toLowerCase().replace(/\s+/g, ''));
        const maybankIndicators = ['masteraccount', 'securitycode', 'maker', 'checker', 'account', 'qty'];
        const maybankMatches = maybankIndicators.filter(indicator =>
            normalizedHeaders.some(h => h.includes(indicator))
        ).length;

        const isMaybankFormat = maybankMatches >= 4;

        if (isMaybankFormat) {
            // Maybank CSV Format
            const fieldMapping = {
                'masteraccount': 'masterAccount',
                'masteraccountname': 'masterAccountName',
                'account': 'account',
                'accountname': 'accountName',
                'securitycode': 'securityCode',
                'qty': 'qty',
                'maker': 'maker',
                'makerdt': 'makerDt',
                'checker': 'checker',
                'checkerdt': 'checkerDt'
            };

            const headerIndexMap = {};
            normalizedHeaders.forEach((header, index) => {
                // Use exact match to avoid confusion between 'account' and 'cashaccount'
                const mappedField = Object.keys(fieldMapping).find(key => header === key);
                if (mappedField) {
                    headerIndexMap[fieldMapping[mappedField]] = index;
                }
            });

            // Check required fields for Maybank format
            const requiredFields = ['masterAccount', 'account', 'securityCode', 'qty'];
            const missingFields = requiredFields.filter(field => !(field in headerIndexMap));

            if (missingFields.length > 0) {
                return {
                    data: [],
                    errors: [`Missing required columns for Maybank CSV: ${missingFields.join(', ')}`],
                    stats: null
                };
            }

            const data = [];
            const errors = [];
            let totalTokenAmount = 0;
            let masterAccountValidated = false;
            let firstRowMasterAccount = null;
            let firstRowSecurityCode = null;

            for (let i = 1; i < lines.length; i++) {
                const line = lines[i].trim();

                if (!line || line.startsWith('#')) continue;

                const values = line.split(',').map(v => v.trim().replace(/"/g, ''));

                const row = {
                    masterAccount: values[headerIndexMap.masterAccount] || '',
                    masterAccountName: values[headerIndexMap.masterAccountName] || '',
                    account: values[headerIndexMap.account] || '',
                    accountName: values[headerIndexMap.accountName] || '',
                    securityCode: values[headerIndexMap.securityCode] || '',
                    qty: values[headerIndexMap.qty] || '',
                    maker: headerIndexMap.maker !== undefined ? values[headerIndexMap.maker] || '' : '',
                    makerDt: headerIndexMap.makerDt !== undefined ? values[headerIndexMap.makerDt] || '' : '',
                    checker: headerIndexMap.checker !== undefined ? values[headerIndexMap.checker] || '' : '',
                    checkerDt: headerIndexMap.checkerDt !== undefined ? values[headerIndexMap.checkerDt] || '' : ''
                };

                // Validate required fields
                if (!row.masterAccount) {
                    errors.push(`Row ${i}: Missing MASTER ACCOUNT`);
                    continue;
                }

                // Store first row's master account and security code for consistency validation
                if (firstRowMasterAccount === null) {
                    firstRowMasterAccount = row.masterAccount;
                }
                if (firstRowSecurityCode === null) {
                    firstRowSecurityCode = row.securityCode;
                }

                // Validate all rows have the same MASTER ACCOUNT
                if (row.masterAccount !== firstRowMasterAccount) {
                    errors.push(`Row ${i}: MASTER ACCOUNT ${row.masterAccount} does not match the first row's MASTER ACCOUNT ${firstRowMasterAccount}. All rows must have the same MASTER ACCOUNT.`);
                    continue;
                }

                // Validate MASTER ACCOUNT matches custodian's Maybank account (only once)
                if (!masterAccountValidated && custodianMasterAccountNo) {
                    if (row.masterAccount !== custodianMasterAccountNo) {
                        errors.push(`MASTER ACCOUNT ${row.masterAccount} does not match your custodian Maybank account ${custodianMasterAccountNo}. Please update the CSV file.`);
                        // Return early as all rows will have the same issue
                        return {
                            data: [],
                            errors,
                            stats: null
                        };
                    }
                    masterAccountValidated = true;
                }

                if (!row.account) {
                    errors.push(`Row ${i}: Missing ACCOUNT (sub-investor account number)`);
                    continue;
                }
                if (!row.securityCode) {
                    errors.push(`Row ${i}: Missing SECURITY CODE (ISIN)`);
                    continue;
                }

                // Validate all rows have the same SECURITY CODE
                if (row.securityCode !== firstRowSecurityCode) {
                    errors.push(`Row ${i}: SECURITY CODE (${row.securityCode}) does not match the first row's SECURITY CODE (${firstRowSecurityCode}). All rows must have the same SECURITY CODE.`);
                    continue;
                }

                // Validate SECURITY CODE matches bond ISIN
                if (bondIsin && row.securityCode !== bondIsin) {
                    errors.push(`Row ${i}: SECURITY CODE (${row.securityCode}) does not match bond ISIN (${bondIsin})`);
                    continue;
                }

                const tokenAmount = parseFloat(row.qty);
                if (isNaN(tokenAmount) || tokenAmount <= 0) {
                    errors.push(`Row ${i}: QTY must be a positive number`);
                    continue;
                }

                totalTokenAmount += tokenAmount;

                data.push({
                    ...row,
                    tokenAmount: tokenAmount
                });
            }

            // Validate total doesn't exceed custodian held tokens
            if (totalTokenAmount > custodianHeldTokens) {
                errors.push(
                    `Total bond amount (${formatToken(totalTokenAmount)}) exceeds custodian held tokens (${formatToken(custodianHeldTokens)}). ` +
                    `Please reduce allocations by ${formatToken(totalTokenAmount - custodianHeldTokens)}.`
                );
            }

            if (data.length === 0 && errors.length === 0) {
                errors.push('No valid data rows found in Maybank CSV file.');
            }

            // Check if all rows have maker/checker for auto-approval
            const hasAllMakerChecker = data.length > 0 && data.every(row =>
                row.maker && row.makerDt && row.checker && row.checkerDt
            );

            return {
                data,
                errors,
                stats: {
                    totalRows: lines.length - 1,
                    validRows: data.length,
                    totalTokenAmount,
                    isMaybankFormat: true,
                    hasAllMakerChecker
                }
            };
        } else {
            // Show error - only Maybank format is supported
            return {
                data: [],
                errors: ['Please use the Maybank CSV format template. Download the template and ensure all required columns are present.'],
                stats: null
            };
        }
    };

    const validateWithBackend = async (parsedAllocations, bondId) => {
        try {
            const response = await custodianAPI.validateHoldingDistribution(bondId, {
                allocations: parsedAllocations
            });
            return response?.data?.data || { valid: true, errors: [], warnings: [] };
        } catch (error) {
            const message = error?.response?.data?.message || error?.message || 'Validation failed';
            return {
                valid: false,
                errors: [message],
                warnings: []
            };
        }
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.name.toLowerCase().endsWith('.csv')) {
            toast.error('Please upload a CSV file');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast.error('File size exceeds 5MB limit');
            return;
        }

        setUploadFile(file);
        setIsUploading(true);
        setUploadProgress(0);
        setCsvErrors([]);
        setCsvWarnings([]);
        setCsvStats(null);
        setParsedData([]);
        setShowPreview(false);

        try {
            // Simulate progress
            setUploadProgress(10);

            const fileContent = await file.text();
            setUploadProgress(30);

            // Parse CSV with bond ISIN validation and custodian master account validation
            const parseResult = parseCSV(
                fileContent,
                uploadContext.custodianHeldTokens,
                uploadContext.bond?.isin,
                effectiveMasterAccountNo
            );
            setUploadProgress(50);

            if (parseResult.errors.length > 0) {
                setCsvErrors(parseResult.errors);
                setCsvStats(parseResult.stats);
                setParsedData(parseResult.data);
                setIsUploading(false);
                return;
            }

            // Validate with backend
            setUploadProgress(70);
            const validationResult = await validateWithBackend(parseResult.data, uploadContext.bondId);
            setUploadProgress(90);

            if (validationResult.errors && validationResult.errors.length > 0) {
                setCsvErrors(validationResult.errors);
            }

            if (validationResult.warnings && validationResult.warnings.length > 0) {
                setCsvWarnings(validationResult.warnings);
            }

            setCsvStats({
                ...parseResult.stats,
                serverValidated: validationResult.valid
            });
            setParsedData(parseResult.data);
            setShowPreview(true);
            setUploadProgress(100);

        } catch (error) {
            const message = error?.message || 'Failed to process CSV file';
            toast.error(message);
            setCsvErrors([message]);
        } finally {
            setIsUploading(false);
        }
    };

    const {
        data: bondSummariesResponse,
        isLoading,
        isFetching,
        refetch: refetchBondSummaries
    } = useQuery({
        queryKey: ['custodian-holding-distributions', 'bonds', currentPage, itemsPerPage, searchTerm],
        queryFn: async () => {
            const response = await custodianAPI.getHoldingDistributionBonds({
                page: currentPage,
                limit: itemsPerPage,
                search: searchTerm
            });
            const root = response?.data || {};
            return {
                data: root.data || [],
                total: root.total || 0,
                page: root.page || 1,
                limit: root.limit || itemsPerPage,
                totalPages: root.totalPages || 0
            };
        },
        staleTime: 0, // Always consider data stale to force refetch
        refetchOnMount: true,
        refetchOnWindowFocus: true,
        keepPreviousData: true // Keep previous data while fetching new page
    });

    const bondSummaries = bondSummariesResponse?.data || [];
    const totalItems = bondSummariesResponse?.total || 0;
    const totalPages = bondSummariesResponse?.totalPages || 0;

    const { data: activeDistribution, isLoading: detailLoading } = useQuery({
        queryKey: ['custodian-holding-distribution', detailsContext?.bondId],
        queryFn: async () => {
            if (!detailsContext) {
                return null;
            }
            const response = await custodianAPI.getHoldingDistribution(detailsContext.bondId);
            return response?.data?.data || null;
        },
        enabled: !!detailsContext
    });

    // Fetch all sub-investor LOC requests for all bonds
    const { data: allSubInvestorData, isLoading: allSubInvestorsLoading, refetch: refetchSubInvestors } = useQuery({
        queryKey: ['custodian-sub-investor-loc-all'],
        queryFn: async () => {
            const response = await allocationRequestsAPI.getCustodianSubInvestorRequests();
            const data = response.data?.data || response.data || { investors: [], summary: {} };
            return data;
        },
        staleTime: 0, // Always consider data stale to force refetch
        refetchOnMount: true,
        refetchOnWindowFocus: true
    });

    // Fetch holdings to track which sub-investors already have approved allocations
    const { data: holdingsData, refetch: refetchHoldings } = useQuery({
        queryKey: ['custodian-holdings-as-custodian'],
        queryFn: async () => {
            const response = await holdingsAPI.getHoldingsAsCustodian({ status: 'active' });
            const holdings = response?.data?.data || [];
            return holdings;
        },
        staleTime: 0,
        refetchOnMount: true,
        refetchOnWindowFocus: true
    });

    const uploadMutation = useMutation({
        mutationFn: async ({ bondId, file }) => {
            const formData = new FormData();
            formData.append('file', file);
            const response = await custodianAPI.uploadHoldingDistribution(bondId, formData);
            return response?.data;
        },
        onSuccess: async (data, variables) => {
            const requiresApproval = data?.data?.requiresApproval || false;

            if (requiresApproval) {
                toast.success('CSV uploaded and staged for L1/L2 approval. Please approve to execute blockchain transfers.');
            } else {
                toast.success('CSV uploaded and allocations completed successfully');
            }

            // Close modal first
            setUploadContext(null);
            setUploadFile(null);
            setCsvErrors([]);
            setCsvWarnings([]);
            setCsvStats(null);
            setParsedData([]);
            setShowPreview(false);

            // Then invalidate and refetch all related queries
            await Promise.all([
                queryClient.invalidateQueries(['custodian-holding-distributions', 'bonds']),
                queryClient.invalidateQueries(['custodian-sub-investor-loc-all']),
                queryClient.invalidateQueries(['custodian-holdings-as-custodian'])
            ]);

            // Force immediate refetch
            setTimeout(async () => {
                await Promise.all([
                    queryClient.refetchQueries(['custodian-holding-distributions', 'bonds']),
                    queryClient.refetchQueries(['custodian-sub-investor-loc-all']),
                    queryClient.refetchQueries(['custodian-holdings-as-custodian'])
                ]);
            }, 500);

            if (detailsContext?.bondId === variables.bondId) {
                await queryClient.invalidateQueries(['custodian-holding-distribution', variables.bondId]);
                await queryClient.refetchQueries(['custodian-holding-distribution', variables.bondId]);
            }
        },
        onError: (error) => {
            const message = error?.response?.data?.message || error?.message || 'Failed to upload CSV';
            toast.error(message);
        }
    });

    const allocationMutation = useMutation({
        mutationFn: async ({ bondId, investorId, amount, lei }) => {
            // Pass hasMakerChecker=false so backend stages allocation for L1/L2 approval
            const response = await custodianAPI.createManualAllocation(bondId, {
                investorId,
                lei,
                tokenAmount: amount,
                hasMakerChecker: false // Always require L1/L2 approval for manual allocations
            });
            return response?.data;
        },
        onSuccess: async (data, variables) => {
            toast.success('Allocation approved and tokens transferred successfully');

            // Invalidate and refetch all related queries
            await Promise.all([
                queryClient.invalidateQueries(['custodian-holding-distributions', 'bonds']),
                queryClient.invalidateQueries(['custodian-sub-investor-loc-all']),
                queryClient.invalidateQueries(['custodian-holding-distribution', variables.bondId]),
                queryClient.invalidateQueries(['custodian-holdings-as-custodian']),
                queryClient.invalidateQueries(['custodian-manual-approval-state', variables.bondId])
            ]);
            // Force refetch to ensure data is updated
            await queryClient.refetchQueries(['custodian-holding-distributions', 'bonds']);
            await queryClient.refetchQueries(['custodian-sub-investor-loc-all']);
            await queryClient.refetchQueries(['custodian-holdings-as-custodian']);
            setEditingInvestor(null);
            setManualAllocations({});
        },
        onError: (error) => {
            const message = error?.response?.data?.message || error?.message || 'Allocation creation failed';
            toast.error(message);
        }
    });

    const bulkAllocationMutation = useMutation({
        mutationFn: async ({ bondId, allocations }) => {
            // Call the bulk allocation endpoint with hasMakerChecker=false to stage for approval
            const response = await custodianAPI.bulkAllocate(bondId, {
                allocations,
                hasMakerChecker: false // Always require L1/L2 approval for manual allocations
            });
            return response?.data;
        },
        onSuccess: async (data, variables) => {
            const { successCount, failureCount, totalAllocations } = data?.data || {};

            if (failureCount > 0) {
                toast.warning(`Bulk allocation completed with issues: ${successCount} succeeded, ${failureCount} failed`);
            } else {
                toast.success(`All ${successCount} allocations completed successfully`);
            }

            // Invalidate and refetch all related queries
            await Promise.all([
                queryClient.invalidateQueries(['custodian-holding-distributions', 'bonds']),
                queryClient.invalidateQueries(['custodian-sub-investor-loc-all']),
                queryClient.invalidateQueries(['custodian-holding-distribution', variables.bondId]),
                queryClient.invalidateQueries(['custodian-holdings-as-custodian']),
                queryClient.invalidateQueries(['custodian-manual-approval-state', variables.bondId])
            ]);
            // Force refetch to ensure data is updated
            await queryClient.refetchQueries(['custodian-holding-distributions', 'bonds']);
            await queryClient.refetchQueries(['custodian-sub-investor-loc-all']);
            await queryClient.refetchQueries(['custodian-holdings-as-custodian']);
            setEditingInvestor(null);
            setManualAllocations({});
        },
        onError: (error) => {
            const message = error?.response?.data?.message || error?.message || 'Bulk allocation failed';
            toast.error(message);
        }
    });

    const approveDistributionMutation = useMutation({
        mutationFn: async ({ bondId, level }) => {
            const response = await custodianAPI.approveHoldingDistribution(bondId, level);
            return response?.data;
        },
        onSuccess: async (data, variables) => {
            const level = variables.level;
            toast.success(`L${level === 'L1' ? '1' : '2'} approval successful`);

            // Invalidate and refetch all related queries
            await Promise.all([
                queryClient.invalidateQueries(['custodian-holding-distributions', 'bonds']),
                queryClient.invalidateQueries(['custodian-sub-investor-loc-all']),
                queryClient.invalidateQueries(['custodian-holding-distribution', variables.bondId]),
                queryClient.invalidateQueries(['custodian-holdings-as-custodian']),
                queryClient.invalidateQueries(['custodian-manual-approval-state', variables.bondId])
            ]);
            // Force refetch to ensure data is updated
            await queryClient.refetchQueries(['custodian-holding-distributions', 'bonds']);
            await queryClient.refetchQueries(['custodian-sub-investor-loc-all']);
            await queryClient.refetchQueries(['custodian-holdings-as-custodian']);
        },
        onError: (error) => {
            const message = error?.response?.data?.message || error?.message || 'Approval failed';
            toast.error(message);
            setConfirmationModal(null);
        }
    });

    const approveManualAllocationMutation = useMutation({
        mutationFn: async ({ bondId, level }) => {
            const response = await custodianAPI.approveManualAllocation(bondId, level);
            return response?.data;
        },
        onSuccess: async (data, variables) => {
            const level = variables.level;
            toast.success(`Manual allocation L${level === 'L1' ? '1' : '2'} approval successful`);

            // Invalidate and refetch all related queries
            await Promise.all([
                queryClient.invalidateQueries(['custodian-holding-distributions', 'bonds']),
                queryClient.invalidateQueries(['custodian-sub-investor-loc-all']),
                queryClient.invalidateQueries(['custodian-holding-distribution', variables.bondId]),
                queryClient.invalidateQueries(['custodian-holdings-as-custodian']),
                queryClient.invalidateQueries(['custodian-manual-approval-state', variables.bondId])
            ]);
            // Force refetch to ensure data is updated
            await queryClient.refetchQueries(['custodian-holding-distributions', 'bonds']);
            await queryClient.refetchQueries(['custodian-sub-investor-loc-all']);
            await queryClient.refetchQueries(['custodian-holdings-as-custodian']);
            setConfirmationModal(null);
        },
        onError: (error) => {
            const message = error?.response?.data?.message || error?.message || 'Manual allocation approval failed';
            toast.error(message);
            setConfirmationModal(null);
        }
    });

    // Track approved allocations based on holdings data
    useEffect(() => {
        if (!holdingsData || !Array.isArray(holdingsData)) return;

        const approved = {};
        holdingsData.forEach(holding => {
            // Only track holdings where the holder is the sub-investor (not custodian)
            // and the source is from manual transfer/allocation
            const holdingBondId = typeof holding.bondId === 'string' ? holding.bondId : holding.bondId?._id || holding.bondId;
            const holdingInvestorId = typeof holding.investorId === 'string' ? holding.investorId : holding.investorId?._id || holding.investorId;
            const holdingHolderId = typeof holding.holderId === 'string' ? holding.holderId : holding.holderId?._id || holding.holderId;

            // Only count holdings where holderId equals investorId (sub-investor is the holder)
            // This filters out custodian-held tokens
            if (holdingBondId && holdingInvestorId &&
                holdingInvestorId === holdingHolderId &&
                holding.remainingTokenAmount > 0) {
                const key = `${holdingBondId}-${holdingInvestorId}`;
                approved[key] = {
                    tokenAmount: holding.tokenAmount,
                    remainingTokenAmount: holding.remainingTokenAmount,
                    holdingId: holding._id,
                    blockchainTxHash: holding.blockchainTxHash,
                    createdAt: holding.createdAt
                };
            }
        });

        setApprovedAllocations(approved);
    }, [holdingsData]);

    if (isLoading || allSubInvestorsLoading) {
        return <LoadingSpinner size="lg" text="Loading bond token allocation..." />;
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="px-4 py-8 mx-auto space-y-6 max-w-7xl sm:px-6 lg:px-8">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Bond Token Allocation</h1>
                        <p className="text-sm text-gray-500">
                            Review custodian-held bond tokens and distribute them to sub-investors
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {isFetching && <span className="text-xs text-gray-500">Refreshing...</span>}
                        <button
                            onClick={downloadTemplate}
                            className="flex items-center space-x-2 btn-primary"
                        >
                            <Download className="w-4 h-4" />
                            <span>Download Template</span>
                        </button>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body">
                        <div className="relative">
                            <Search className="absolute w-4 h-4 text-gray-400 transform -translate-y-1/2 left-3 top-1/2" />
                            <input
                                type="text"
                                className="w-full pl-10 form-input"
                                placeholder="Search bonds by name, ISIN, or number"
                                value={searchTerm}
                                onChange={(event) => {
                                    setSearchTerm(event.target.value);
                                    setCurrentPage(1); // Reset to first page on search
                                }}
                            />
                        </div>
                    </div>
                </div>

                <div className="space-y-6">
                    {bondSummaries.length === 0 && !isLoading && (
                        <div className="card">
                            <div className="py-10 text-center card-body">
                                <div className="flex flex-col items-center text-gray-500">
                                    <FileText className="w-12 h-12 mb-4 text-gray-300" />
                                    <p className="text-lg font-medium">No bonds found</p>
                                    <p className="text-sm">Adjust your search or confirm there are holdings under this custodian.</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {bondSummaries.map((summary) => {
                        const { bond, custodianHeldTokens, latestDistribution, bondId, manualApprovalState } = summary;

                        // Filter sub-investors from LOC requests for this specific bond
                        const locSubInvestors = (allSubInvestorData?.investors || []).filter(inv => {
                            return inv.bonds?.some(b => {
                                const bId = typeof b.bondId === 'string' ? b.bondId : b.bondId?._id;
                                return bId === bondId;
                            });
                        }).map(investor => {
                            const bondData = investor.bonds?.find(b => {
                                const bId = typeof b.bondId === 'string' ? b.bondId : b.bondId?._id;
                                return bId === bondId;
                            });
                            return {
                                ...investor,
                                name: investor.name || investor.corporateInfo?.companyName || investor.corporateInfo?.legalEntityName || investor.email || 'Unnamed',
                                bondData,
                                source: 'loc' // Mark as LOC-based
                            };
                        });

                        // Add investors from CSV allocations (from distribution rows)
                        const csvInvestorMap = new Map();
                        if (latestDistribution?.rows && Array.isArray(latestDistribution.rows)) {
                            latestDistribution.rows.forEach(row => {
                                const investorIdStr = typeof row.investorId === 'string' ? row.investorId : row.investorId?.toString();
                                if (investorIdStr && row.status === 'completed') {
                                    csvInvestorMap.set(investorIdStr, {
                                        userId: investorIdStr,
                                        name: row.investorName || row.investorEmail || 'Unnamed',
                                        email: row.investorEmail,
                                        lei: row.lei,
                                        corporateInfo: {
                                            companyName: row.investorName
                                        },
                                        bondData: {
                                            totalAmount: row.tokenAmount,
                                            bondId: bondId
                                        },
                                        source: 'csv', // Mark as CSV-based
                                        blockchainTxHash: row.blockchainTxHash
                                    });
                                }
                            });
                        }

                        // Merge LOC investors with CSV investors (CSV takes precedence if already allocated)
                        const mergedInvestorsMap = new Map();

                        // First add LOC investors
                        locSubInvestors.forEach(inv => {
                            mergedInvestorsMap.set(inv.userId, inv);
                        });

                        // Then add/override with CSV investors
                        csvInvestorMap.forEach((inv, userId) => {
                            mergedInvestorsMap.set(userId, inv);
                        });

                        const bondSubInvestors = Array.from(mergedInvestorsMap.values());

                        // Check if all allocations are completed for this bond
                        const allAllocationsCompleted = custodianHeldTokens === 0 && bondSubInvestors.every(investor => {
                            const investorId = investor.userId;
                            const editKey = `${bondId}-${investorId}`;
                            return approvedAllocations[editKey];
                        });

                        return (
                            <div key={bondId} className="bg-white border border-gray-200 rounded-lg shadow-sm">
                                {/* Bond Header */}
                                <div className="px-6 py-4 border-b border-gray-200">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="flex items-center space-x-2">
                                                <h3 className="text-lg font-medium text-gray-900">{bond?.bondName || 'Unnamed Bond'}</h3>
                                                {bond?.tokenDetails?.symbol && (
                                                    <span className="px-2 py-0.5 text-xs font-semibold bg-primary-100 text-primary-800 rounded">
                                                        {bond.tokenDetails.symbol}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center mt-1 space-x-4 text-sm text-gray-500">
                                                <span>ISIN: {bond?.isin || '—'}</span>
                                                <span>•</span>
                                                <span>Bond #: {bond?.bondNumber || '—'}</span>
                                                <span>•</span>
                                                <span>{bond?.productType === "Fixed Notes" ? "Conventional" : (bond?.productType || '—')}</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-3">
                                            {/* Show CSV distribution status badge */}
                                            {latestDistribution && (
                                                <div className="flex items-center gap-2">
                                                    {renderStatusBadge(latestDistribution)}
                                                </div>
                                            )}
                                            {/* Show manual allocation status badge if there's manual approval state */}
                                            {manualApprovalState && manualApprovalState.status && manualApprovalState.status !== 'Pending L1' && !latestDistribution && (
                                                <div className="flex items-center gap-2">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${manualApprovalState.status === 'Pending L2' ? 'bg-orange-100 text-orange-800' :
                                                        manualApprovalState.status === 'Approved' ? 'bg-green-100 text-green-800' :
                                                            'bg-yellow-100 text-yellow-800'
                                                        }`}>
                                                        <Clock className="w-3.5 h-3.5 mr-1" />
                                                        {manualApprovalState.status}
                                                    </span>
                                                </div>
                                            )}

                                            {/* Show L1 Approval button for CSV distribution if staged */}
                                            {latestDistribution?.status === 'staged' && (
                                                <button
                                                    className="flex items-center space-x-1 btn-primary btn-sm"
                                                    onClick={() => {
                                                        setConfirmationModal({
                                                            type: 'l1',
                                                            bondId: summary.bondId,
                                                            bondName: summary.bondName,
                                                            message: `You are about to perform L1 approval for the CSV distribution of bond "${summary.bondName}". After L1 approval, a different user must perform L2 approval to execute the distribution.`,
                                                            isManual: false
                                                        });
                                                    }}
                                                    disabled={approveDistributionMutation.isLoading}
                                                >
                                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                                    <span>{approveDistributionMutation.isLoading ? 'Approving...' : 'CSV L1'}</span>
                                                </button>
                                            )}

                                            {/* Show L2 Approval button for CSV distribution if L1 approved and current user is not L1 approver */}
                                            {latestDistribution?.status === 'l1_approved' &&
                                                latestDistribution?.l1ApprovedBy &&
                                                String(latestDistribution.l1ApprovedBy._id || latestDistribution.l1ApprovedBy) !== String(user?.id || user?._id) && (
                                                    <button
                                                        className="flex items-center space-x-1 btn-primary btn-sm"
                                                        onClick={() => {
                                                            setConfirmationModal({
                                                                type: 'l2',
                                                                bondId: summary.bondId,
                                                                bondName: summary.bondName,
                                                                message: `You are about to perform L2 approval for the bond "${summary.bondName}". This will execute the distribution.`,
                                                                isManual: false
                                                            });
                                                        }}
                                                    >
                                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                                        <span>{approveDistributionMutation.isLoading ? 'Executing...' : 'CSV L2'}</span>
                                                    </button>
                                                )}

                                            {!allAllocationsCompleted && (
                                                <button
                                                    className={`btn-secondary btn-sm flex items-center space-x-1 `}
                                                    onClick={() => {
                                                        setUploadContext(summary);
                                                        setUploadFile(null);
                                                        setCsvErrors([]);
                                                        setCsvWarnings([]);
                                                        setCsvStats(null);
                                                        setParsedData([]);
                                                        setShowPreview(false);
                                                    }}
                                                >
                                                    <Upload className="w-3.5 h-3.5" />
                                                    <span>Upload CSV</span>
                                                </button>
                                            )}
                                            {latestDistribution && (
                                                <button
                                                    className="flex items-center space-x-1 btn-outline btn-sm"
                                                    onClick={() => setDetailsContext({ bondId, bond })}
                                                >
                                                    <FileText className="w-3.5 h-3.5" />
                                                    <span>Details</span>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Bond Summary */}
                                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                                    {custodianHeldTokens === 0 && (
                                        <div className="flex items-start p-3 mb-4 border border-blue-200 rounded-md bg-blue-50">
                                            <CheckCircle2 className="h-4 w-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                                            <div className="text-xs text-blue-800">
                                                <strong>Fully Allocated:</strong> All tokens for this bond have been distributed to sub-investors.
                                            </div>
                                        </div>
                                    )}
                                    <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
                                        <div>
                                            <span className="text-gray-600">Custodian Held:</span>
                                            <div className="font-medium">{formatToken(custodianHeldTokens)}</div>
                                            <div className="text-xs text-gray-500">
                                                {custodianHeldTokens === 0 ? 'Fully allocated' : 'Current remaining balance'}
                                            </div>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Sub-Investors:</span>
                                            <div className="font-medium">{bondSubInvestors.length}</div>
                                            <div className="text-xs text-gray-500">Total sub-investors</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Sub-Investors Table */}
                                {bondSubInvestors.length > 0 ? (
                                    <>
                                        <div className="flex items-center justify-between px-6 py-3 bg-white border-b border-gray-200">
                                            <div className="text-sm text-gray-600">
                                                {bondSubInvestors.length} sub-investor{bondSubInvestors.length > 1 ? 's' : ''} found
                                            </div>
                                            {/* Show Bulk Approve button if not all allocations completed */}
                                            {!allAllocationsCompleted && (
                                                <button
                                                    onClick={() => {
                                                        // Only create allocations for non-approved investors
                                                        const allocations = bondSubInvestors
                                                            .filter(investor => {
                                                                const investorId = investor.userId;
                                                                const editKey = `${bondId}-${investorId}`;
                                                                return !approvedAllocations[editKey]; // Skip already approved
                                                            })
                                                            .map(investor => {
                                                                const investorId = investor.userId;
                                                                const editKey = `${bondId}-${investorId}`;
                                                                const defaultAllocation = investor.bondData?.totalAmount || 0;
                                                                const amount = manualAllocations[editKey] !== undefined
                                                                    ? parseFloat(manualAllocations[editKey])
                                                                    : defaultAllocation;

                                                                return {
                                                                    investorId,
                                                                    lei: investor.lei,
                                                                    tokenAmount: amount
                                                                };
                                                            });

                                                        if (allocations.length === 0) {
                                                            toast.info('All sub-investors have already been allocated');
                                                            return;
                                                        }

                                                        // Validate total doesn't exceed custodian held
                                                        const totalAmount = allocations.reduce((sum, a) => sum + a.tokenAmount, 0);
                                                        if (totalAmount > custodianHeldTokens) {
                                                            toast.error(`Total allocation (${formatToken(totalAmount)}) exceeds custodian held tokens (${formatToken(custodianHeldTokens)})`);
                                                            return;
                                                        }

                                                        // Check for invalid amounts
                                                        const hasInvalidAmounts = allocations.some(a => isNaN(a.tokenAmount) || a.tokenAmount <= 0);
                                                        if (hasInvalidAmounts) {
                                                            toast.error('All allocation amounts must be valid positive numbers');
                                                            return;
                                                        }

                                                        // Bulk Approve and Execute
                                                        bulkAllocationMutation.mutate({ bondId, allocations });
                                                    }}
                                                    disabled={bulkAllocationMutation.isLoading}
                                                    className={`btn-primary btn-sm flex items-center gap-1 `}
                                                >
                                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                                    <span>{bulkAllocationMutation.isLoading ? 'Processing...' : 'Bulk Approve'}</span>
                                                </button>
                                            )}
                                        </div>
                                        <div className="overflow-x-auto">
                                            <table className="table">
                                                <thead className="table-header">
                                                    <tr>
                                                        <th className="table-header-cell">Sub-Investor</th>
                                                        <th className="table-header-cell">Email</th>
                                                        <th className="table-header-cell">Token Allocation</th>
                                                        <th className="table-header-cell">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {bondSubInvestors.map((investor) => {
                                                        const investorId = investor.userId;
                                                        const editKey = `${bondId}-${investorId}`;
                                                        const isEditing = editingInvestor === editKey;
                                                        const defaultAllocation = investor.bondData?.totalAmount || 0;
                                                        const currentAllocation = manualAllocations[editKey] !== undefined
                                                            ? manualAllocations[editKey]
                                                            : defaultAllocation;
                                                        const approvalInfo = approvedAllocations[editKey];
                                                        const isApproved = !!approvalInfo;

                                                        return (
                                                            <tr key={investorId} className="hover:bg-gray-50">
                                                                <td className="px-6 py-4">
                                                                    <div className="flex items-center gap-2">
                                                                        <div>
                                                                            <div className="text-sm font-medium text-gray-900">
                                                                                {investor.name || 'Unnamed'}
                                                                            </div>
                                                                            <div className="font-mono text-xs text-gray-500">ID: {investor.userId}</div>
                                                                        </div>
                                                                        {isApproved && (
                                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                                                                Approved
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td className="px-6 py-4 text-sm text-gray-600">
                                                                    {investor.email || '—'}
                                                                </td>
                                                                <td className="px-6 py-4">
                                                                    {isApproved ? (
                                                                        <div>
                                                                            <div className="text-sm font-medium text-green-700">
                                                                                {formatCurrency(approvalInfo.remainingTokenAmount)}
                                                                            </div>
                                                                            <div className="text-xs text-gray-500">
                                                                                Allocated on {new Date(approvalInfo.createdAt).toLocaleDateString()}
                                                                            </div>
                                                                        </div>
                                                                    ) : isEditing ? (
                                                                        <input
                                                                            type="number"
                                                                            min="0"
                                                                            step="0.0001"
                                                                            value={currentAllocation}
                                                                            onChange={(e) => {
                                                                                setManualAllocations(prev => ({
                                                                                    ...prev,
                                                                                    [editKey]: e.target.value
                                                                                }));
                                                                            }}
                                                                            className="w-32 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                                                                            autoFocus
                                                                        />
                                                                    ) : (
                                                                        <div className="text-sm font-medium text-gray-900">
                                                                            {formatCurrency(currentAllocation)}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td className="px-6 py-4">
                                                                    {isApproved ? (
                                                                        <div className="text-xs text-gray-500">
                                                                            Allocation completed
                                                                            {approvalInfo.blockchainTxHash && (
                                                                                <div className="mt-1 font-mono">
                                                                                    TX: {approvalInfo.blockchainTxHash.slice(0, 10)}...
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    ) : (
                                                                        <div className="flex items-center gap-2">
                                                                            {isEditing ? (
                                                                                <>
                                                                                    <button
                                                                                        disabled={!currentAllocation || parseFloat(currentAllocation) <= 0}
                                                                                        onClick={() => {
                                                                                            const amount = parseFloat(currentAllocation);
                                                                                            if (isNaN(amount) || amount <= 0) {
                                                                                                toast.error('Please enter a valid bond amount');
                                                                                                return;
                                                                                            }

                                                                                            // Calculate total PENDING allocations for this bond (exclude approved)
                                                                                            const totalAllocated = bondSubInvestors.reduce((sum, inv) => {
                                                                                                const invId = inv.userId;
                                                                                                const invEditKey = `${bondId}-${invId}`;
                                                                                                // Skip if already approved
                                                                                                if (approvedAllocations[invEditKey]) {
                                                                                                    return sum;
                                                                                                }
                                                                                                // Use the new amount if it's the current investor, otherwise use existing allocation
                                                                                                const invAmount = invEditKey === editKey
                                                                                                    ? amount
                                                                                                    : (manualAllocations[invEditKey] !== undefined
                                                                                                        ? parseFloat(manualAllocations[invEditKey])
                                                                                                        : (inv.bondData?.totalAmount || 0));
                                                                                                return sum + invAmount;
                                                                                            }, 0);

                                                                                            if (totalAllocated > custodianHeldTokens) {
                                                                                                toast.error(
                                                                                                    `Total allocation (${formatToken(totalAllocated)}) exceeds custodian held tokens (${formatToken(custodianHeldTokens)})`
                                                                                                );
                                                                                                return;
                                                                                            }

                                                                                            // Store the parsed number value
                                                                                            setManualAllocations(prev => ({
                                                                                                ...prev,
                                                                                                [editKey]: amount
                                                                                            }));
                                                                                            setEditingInvestor(null);
                                                                                            toast.success('Allocation amount updated');
                                                                                        }}
                                                                                        className="flex items-center gap-1 btn-primary btn-sm"
                                                                                    >
                                                                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                                                                        Confirm
                                                                                    </button>
                                                                                    <button
                                                                                        onClick={() => {
                                                                                            setEditingInvestor(null);
                                                                                            setManualAllocations(prev => {
                                                                                                const updated = { ...prev };
                                                                                                delete updated[editKey];
                                                                                                return updated;
                                                                                            });
                                                                                        }}
                                                                                        className="btn-outline btn-sm"
                                                                                    >
                                                                                        Cancel
                                                                                    </button>
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <button
                                                                                        onClick={true ? () => setEditingInvestor(editKey) : undefined}

                                                                                        className={`btn-outline btn-sm flex items-center justify-center gap-1 w-28`}
                                                                                    >
                                                                                        <Eye className="w-3.5 h-3.5" />
                                                                                        Edit
                                                                                    </button>
                                                                                    {/* Show Approve & Transfer button */}
                                                                                    <button
                                                                                        onClick={() => {
                                                                                            const amount = parseFloat(currentAllocation);
                                                                                            if (isNaN(amount) || amount <= 0) {
                                                                                                toast.error('Please enter a valid bond amount');
                                                                                                return;
                                                                                            }

                                                                                            // Calculate total PENDING allocations for this bond (exclude approved)
                                                                                            const totalAllocated = bondSubInvestors.reduce((sum, inv) => {
                                                                                                const invId = inv.userId;
                                                                                                const invEditKey = `${bondId}-${invId}`;
                                                                                                // Skip if already approved
                                                                                                if (approvedAllocations[invEditKey]) {
                                                                                                    return sum;
                                                                                                }
                                                                                                // Use the new amount if it's the current investor, otherwise use existing allocation
                                                                                                const invAmount = invId === investorId
                                                                                                    ? amount
                                                                                                    : (manualAllocations[invEditKey] !== undefined
                                                                                                        ? parseFloat(manualAllocations[invEditKey])
                                                                                                        : (inv.bondData?.totalAmount || 0));
                                                                                                return sum + invAmount;
                                                                                            }, 0);

                                                                                            if (totalAllocated > custodianHeldTokens) {
                                                                                                toast.error(
                                                                                                    `Total allocation (${formatToken(totalAllocated)}) would exceed custodian held tokens (${formatToken(custodianHeldTokens)})`
                                                                                                );
                                                                                                return;
                                                                                            }

                                                                                            // Approve and execute transfer
                                                                                            allocationMutation.mutate({ bondId, investorId, amount, lei: investor.lei });
                                                                                        }}
                                                                                        disabled={allocationMutation.isLoading}
                                                                                        className={`btn-primary btn-sm flex items-center justify-center gap-1 w-40`}
                                                                                    >
                                                                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                                                                        {allocationMutation.isLoading ? 'Processing...' : 'Approve'}
                                                                                    </button>
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                ) : (
                                    <div className="px-6 py-12 text-center bg-gray-50">
                                        <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                                        <h3 className="mb-1 text-sm font-medium text-gray-900">No Sub-Investors Yet</h3>
                                        <p className="text-xs text-gray-600">
                                            No sub-investors have submitted LOC documents for this bond. Use "Upload CSV" to create allocations.
                                        </p>
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="card">
                            <div className="card-body">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                        <span>Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} bonds</span>
                                    </div>
                                    <div className="flex flex-col sm:flex-row items-center gap-4">
                                        <div className="flex items-center gap-3">
                                            <label className="text-sm text-gray-600 whitespace-nowrap">Items per page:</label>
                                            <select
                                                value={itemsPerPage}
                                                onChange={(e) => {
                                                    setItemsPerPage(Number(e.target.value));
                                                    setCurrentPage(1);
                                                }}
                                                className="form-select text-sm py-1.5 pl-3 pr-8 border-gray-300 rounded focus:ring-2 focus:ring-primary-500"
                                            >
                                                <option value={5}>5</option>
                                                <option value={10}>10</option>
                                                <option value={20}>20</option>
                                                <option value={50}>50</option>
                                            </select>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                                disabled={currentPage === 1}
                                                className="p-2 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                            >
                                                <ChevronLeft className="w-4 h-4" />
                                            </button>
                                            <div className="flex items-center gap-2">
                                                {[...Array(totalPages)].map((_, idx) => {
                                                    const pageNum = idx + 1;
                                                    // Show first page, last page, current page, and pages around current
                                                    if (
                                                        pageNum === 1 ||
                                                        pageNum === totalPages ||
                                                        (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                                                    ) {
                                                        return (
                                                            <button
                                                                key={pageNum}
                                                                onClick={() => setCurrentPage(pageNum)}
                                                                className={`min-w-[2.5rem] px-3 py-1.5 rounded border text-sm font-medium transition-colors ${currentPage === pageNum
                                                                    ? 'bg-primary-600 text-white border-primary-600'
                                                                    : 'border-gray-300 hover:bg-gray-50 text-gray-700'
                                                                    }`}
                                                            >
                                                                {pageNum}
                                                            </button>
                                                        );
                                                    } else if (
                                                        pageNum === currentPage - 2 ||
                                                        pageNum === currentPage + 2
                                                    ) {
                                                        return <span key={pageNum} className="px-2 text-gray-400">...</span>;
                                                    }
                                                    return null;
                                                })}
                                            </div>
                                            <button
                                                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                                disabled={currentPage === totalPages}
                                                className="p-2 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                            >
                                                <ChevronRight className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Upload Modal */}
            {uploadContext && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-medium text-gray-900">
                                    Upload Bond Token Distribution for {uploadContext.bond?.bondName || uploadContext.bondId}
                                </h3>
                                <button
                                    onClick={() => {
                                        setUploadContext(null);
                                        setUploadFile(null);
                                        setCsvErrors([]);
                                        setCsvWarnings([]);
                                        setCsvStats(null);
                                        setParsedData([]);
                                        setShowPreview(false);
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                        </div>

                        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
                            {/* Bond Summary */}
                            <div className="p-4 mb-6 rounded-lg bg-gray-50">
                                <h4 className="mb-3 text-sm font-medium text-gray-900">Bond Summary</h4>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="text-gray-600">Bond Name:</span>
                                        <div className="font-medium">{uploadContext.bond?.bondName || '—'}</div>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">ISIN:</span>
                                        <div className="font-medium">{uploadContext.bond?.isin || '—'}</div>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Custodian Held Tokens:</span>
                                        <div className="font-medium">{formatToken(uploadContext.custodianHeldTokens)}</div>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Product Type:</span>
                                        <div className="font-medium">{uploadContext.bond?.productType === "Fixed Notes" ? "Conventional" : (uploadContext.bond?.productType || '—')}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Upload Instructions */}
                            <div className="p-4 mb-6 border border-blue-200 rounded-md bg-blue-50">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <h4 className="mb-2 text-sm font-medium text-blue-900">Maybank CSV Upload Instructions</h4>
                                        <ul className="space-y-1 text-xs text-blue-800 list-disc list-inside">
                                            <li>Required columns: <strong>MASTER ACCOUNT</strong>, <strong>ACCOUNT</strong>, <strong>SECURITY CODE</strong>, <strong>QTY</strong></li>
                                            <li><strong>MASTER ACCOUNT:</strong> Custodian Maybank account number (must match your account)</li>
                                            <li><strong>ACCOUNT:</strong> Sub-investor Maybank custody account number</li>
                                            <li><strong>SECURITY CODE:</strong> Bond ISIN (must match: <strong>{uploadContext.bond?.isin || '—'}</strong>)</li>
                                            <li><strong>QTY:</strong> Bond amount to allocate</li>
                                            <li className="font-medium text-orange-800">
                                                <strong>⚠️ Approval Workflow:</strong> If MAKER, MAKERDT, CHECKER, and CHECKERDT are <strong>all provided</strong>, blockchain transfers will execute immediately. Otherwise, L1 and L2 approval will be required before executing transfers.
                                            </li>
                                            <li>Total QTY cannot exceed custodian held tokens</li>
                                            <li>File must be in CSV format, maximum size 5MB</li>
                                            <li><strong>Note:</strong> All rows must be valid or the entire upload will be rejected</li>
                                        </ul>
                                    </div>
                                    <button
                                        onClick={downloadTemplate}
                                        className="flex items-center ml-4 space-x-1 btn-outline btn-sm"
                                    >
                                        <Download className="w-3 h-3" />
                                        <span>Template</span>
                                    </button>
                                </div>
                            </div>

                            {/* Warning if no Maybank master account configured */}
                            {!hasMasterAccountConfigured && (
                                <div className="p-4 mb-6 border border-yellow-200 rounded-md bg-yellow-50">
                                    <div className="flex items-start">
                                        <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-2" />
                                        <div>
                                            <h4 className="mb-1 text-sm font-medium text-yellow-900">Maybank Master Account Not Configured</h4>
                                            <p className="text-xs text-yellow-800">
                                                {user?.parentAccountId
                                                    ? 'Neither your account nor your parent custodian account has a Maybank Master Account Number configured.'
                                                    : 'Your custodian account does not have a Maybank Master Account Number configured.'
                                                } Please contact your administrator to set up the Maybank Master Account Number before uploading CSV files.
                                                CSV validation will not be able to verify the MASTER ACCOUNT field without this configuration.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* File Upload Section */}
                            <div className="mb-6">
                                <label className="block mb-2 text-sm font-medium text-gray-700">
                                    Select CSV File
                                </label>
                                <input
                                    type="file"
                                    accept=".csv"
                                    className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none"
                                    onChange={handleFileUpload}
                                />
                                {uploadFile && (
                                    <div className="flex items-center mt-2 text-xs text-gray-500">
                                        <FileText className="w-4 h-4 mr-1" />
                                        Selected: {uploadFile.name} ({Math.round(uploadFile.size / 1024)} KB)
                                    </div>
                                )}
                                {isUploading && (
                                    <div className="mt-3">
                                        <div className="flex items-center justify-between mb-1 text-xs text-gray-600">
                                            <span>Processing CSV...</span>
                                            <span>{uploadProgress}%</span>
                                        </div>
                                        <div className="w-full h-2 bg-gray-200 rounded-full">
                                            <div
                                                className="h-2 transition-all duration-300 bg-blue-600 rounded-full"
                                                style={{ width: `${uploadProgress}%` }}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* CSV Errors */}
                            {csvErrors.length > 0 && (
                                <div className="p-4 mb-6 border border-red-200 rounded-md bg-red-50">
                                    <div className="flex items-start">
                                        <AlertCircle className="h-5 w-5 text-red-600 mr-2 mt-0.5" />
                                        <div className="flex-1">
                                            <h4 className="mb-2 text-sm font-medium text-red-800">
                                                {csvErrors.length === 1 ? 'CSV Validation Issue' : `${csvErrors.length} CSV Validation Issues`}
                                            </h4>
                                            <div className="space-y-1 overflow-y-auto text-xs text-red-700 max-h-32">
                                                {csvErrors.map((error, index) => (
                                                    <div key={index}>• {error}</div>
                                                ))}
                                            </div>
                                            <div className="p-2 mt-2 text-xs text-orange-700 border border-orange-200 rounded bg-orange-50">
                                                Fix all issues above before you can upload the distribution.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* CSV Warnings */}
                            {csvWarnings.length > 0 && (
                                <div className="p-4 mb-6 border border-yellow-200 rounded-md bg-yellow-50">
                                    <div className="flex items-start">
                                        <AlertCircle className="h-5 w-5 text-yellow-600 mr-2 mt-0.5" />
                                        <div className="flex-1">
                                            <h4 className="mb-2 text-sm font-medium text-yellow-800">
                                                {csvWarnings.length === 1 ? 'Validation Warning' : `${csvWarnings.length} Validation Warnings`}
                                            </h4>
                                            <div className="space-y-1 overflow-y-auto text-xs text-yellow-700 max-h-32">
                                                {csvWarnings.map((warning, index) => (
                                                    <div key={index} className="flex items-start">
                                                        <span className="mr-1">⚠️</span>
                                                        <span>{warning}</span>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="p-2 mt-2 text-xs text-yellow-700 bg-yellow-100 border border-yellow-300 rounded">
                                                These are warnings that don't prevent upload, but you should review them carefully.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* CSV Stats */}
                            {csvStats && csvErrors.length === 0 && (
                                <div className="p-4 mb-6 border border-green-200 rounded-md bg-green-50">
                                    <div className="flex items-start">
                                        <CheckCircle2 className="h-5 w-5 text-green-600 mr-2 mt-0.5" />
                                        <div className="flex-1">
                                            <h4 className="mb-2 text-sm font-medium text-green-800">
                                                {csvStats.isMaybankFormat ? 'Maybank CSV Processing Summary' : 'CSV Processing Summary'}
                                            </h4>
                                            <div className="grid grid-cols-2 gap-4 text-xs text-green-700">
                                                <div>Total Rows: {csvStats.totalRows}</div>
                                                <div>Valid Rows: {csvStats.validRows}</div>
                                                <div>Total Bond Amount: {formatToken(csvStats.totalTokenAmount)}</div>
                                                <div>Remaining with Custodian: {formatToken(uploadContext.custodianHeldTokens - csvStats.totalTokenAmount)}</div>
                                            </div>
                                            {csvStats.isMaybankFormat && csvStats.hasAllMakerChecker && (
                                                <div className="p-2 mt-2 text-xs text-blue-700 border border-blue-200 rounded bg-blue-50">
                                                    ✅ <strong>Auto-Approval Enabled:</strong> All rows have MAKER and CHECKER fields. L1/L2 approval will be skipped.
                                                </div>
                                            )}
                                            {csvStats.isMaybankFormat && !csvStats.hasAllMakerChecker && (
                                                <div className="p-2 mt-2 text-xs text-orange-700 border border-orange-200 rounded bg-orange-50">
                                                    ⚠️ <strong>Manual Approval Required:</strong> Some rows are missing MAKER/CHECKER fields. L1/L2 approval will be required after upload.
                                                </div>
                                            )}
                                            {csvStats.serverValidated && (
                                                <div className="p-2 mt-2 text-xs text-blue-700 border border-blue-200 rounded bg-blue-50">
                                                    ✅ <strong>Database Validation:</strong> Sub-investors and bond ISIN have been validated. Click "Upload & Allocate" to proceed.
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Preview Table */}
                            {showPreview && parsedData.length > 0 && (
                                <div className="mb-6">
                                    <h4 className="mb-3 text-sm font-medium text-gray-900">Distribution Preview</h4>
                                    <div className="overflow-x-auto border border-gray-200 rounded-md">
                                        <table className="table">
                                            <thead className="table-header">
                                                <tr>
                                                    {csvStats?.isMaybankFormat ? (
                                                        <>
                                                            <th className="table-header-cell">Account</th>
                                                            <th className="table-header-cell">Account Name</th>
                                                            <th className="table-header-cell">Security Code</th>
                                                            <th className="table-header-cell">QTY</th>
                                                            <th className="table-header-cell">Maker</th>
                                                            <th className="table-header-cell">Checker</th>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <th className="table-header-cell">Investor ID</th>
                                                            <th className="table-header-cell">LEI</th>
                                                            <th className="table-header-cell">Bond Amount</th>
                                                        </>
                                                    )}
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {parsedData.map((row, index) => (
                                                    <tr key={index} className="hover:bg-gray-50">
                                                        {csvStats?.isMaybankFormat ? (
                                                            <>
                                                                <td className="px-3 py-2 text-sm text-gray-900">{row.account}</td>
                                                                <td className="px-3 py-2 text-sm text-gray-600">{row.accountName || '—'}</td>
                                                                <td className="px-3 py-2 font-mono text-sm text-gray-900">{row.securityCode}</td>
                                                                <td className="px-3 py-2 text-sm text-right text-gray-900">{formatToken(row.tokenAmount)}</td>
                                                                <td className="px-3 py-2 text-sm text-gray-600">{row.maker || '—'}</td>
                                                                <td className="px-3 py-2 text-sm text-gray-600">{row.checker || '—'}</td>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <td className="px-3 py-2 font-mono text-sm text-gray-900">{row.investorId}</td>
                                                                <td className="px-3 py-2 font-mono text-sm text-gray-500">{row.lei}</td>
                                                                <td className="px-3 py-2 text-sm text-right text-gray-900">{formatToken(row.tokenAmount)}</td>
                                                            </>
                                                        )}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-between px-6 py-4 border-t border-gray-200">
                            <button
                                onClick={() => {
                                    setUploadContext(null);
                                    setUploadFile(null);
                                    setCsvErrors([]);
                                    setCsvWarnings([]);
                                    setCsvStats(null);
                                    setParsedData([]);
                                    setShowPreview(false);
                                }}
                                disabled={uploadMutation.isLoading}
                                className="btn-outline"
                            >
                                Cancel
                            </button>
                            <button
                                disabled={!uploadFile || uploadMutation.isLoading || csvErrors.length > 0 || !showPreview}
                                onClick={() => {
                                    if (!uploadFile) {
                                        toast.error('Please select a CSV file');
                                        return;
                                    }
                                    if (csvErrors.length > 0) {
                                        toast.error('Please fix CSV errors before uploading');
                                        return;
                                    }
                                    uploadMutation.mutate({ bondId: uploadContext.bondId, file: uploadFile });
                                }}
                                className="btn-primary"
                            >
                                {uploadMutation.isLoading ? (
                                    <>
                                        <Clock className="w-4 h-4 mr-2 animate-spin" />
                                        {csvStats?.hasAllMakerChecker ? 'Uploading & Executing...' : 'Uploading & Staging...'}
                                    </>
                                ) : (
                                    <>
                                        <Upload className="w-4 h-4 mr-2" />
                                        {csvStats?.hasAllMakerChecker ? 'Upload & Execute' : 'Upload & Stage for Approval'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Details Modal */}
            {detailsContext && (
                <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/40">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
                        <div className="flex items-center justify-between px-6 py-4 border-b">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">Distribution Details</h3>
                                <p className="mt-1 text-xs text-gray-500">Bond: {detailsContext.bond?.bondName || detailsContext.bondId}</p>
                            </div>
                            <button
                                className="text-gray-400 hover:text-gray-600"
                                onClick={() => setDetailsContext(null)}
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="px-6 py-4 overflow-y-auto">
                            {detailLoading ? (
                                <LoadingSpinner size="sm" text="Loading distribution..." />
                            ) : activeDistribution ? (
                                <div className="space-y-6">
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        <div className="card">
                                            <div className="space-y-2 card-body">
                                                <h4 className="text-sm font-semibold text-gray-700">Workflow</h4>
                                                <div className="flex items-center space-x-2">
                                                    {renderStatusBadge(activeDistribution)}
                                                    <span className="text-xs text-gray-500">{activeDistribution.rowCount} rows</span>
                                                </div>
                                                <div className="space-y-1 text-xs text-gray-600">
                                                    <div>Total tokens: {formatToken(activeDistribution.totalTokenAmount)}</div>
                                                    <div>Uploaded: {activeDistribution.uploadedAt ? formatDate(activeDistribution.uploadedAt) : '—'}</div>
                                                    <div>L1 Approved: {activeDistribution.l1ApprovedAt ? formatDate(activeDistribution.l1ApprovedAt) : '—'}</div>
                                                    <div>L2 Approved: {activeDistribution.l2ApprovedAt ? formatDate(activeDistribution.l2ApprovedAt) : '—'}</div>
                                                    <div>Executed: {activeDistribution.executedAt ? formatDate(activeDistribution.executedAt) : '—'}</div>
                                                </div>
                                                {activeDistribution.failureReason && (
                                                    <div className="flex items-center text-xs text-red-500">
                                                        <AlertCircle className="w-3.5 h-3.5 mr-1" />
                                                        {activeDistribution.failureReason}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="card">
                                            <div className="space-y-1 text-xs text-gray-600 card-body">
                                                <h4 className="text-sm font-semibold text-gray-700">Participants</h4>
                                                <div>Uploaded by: {activeDistribution.uploadedBy?.email || '—'}</div>
                                                <div>L1 approver: {activeDistribution.l1ApprovedBy?.email || '—'}</div>
                                                <div>L2 approver: {activeDistribution.l2ApprovedBy?.email || '—'}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="card">
                                        <div className="card-header">
                                            <h4 className="text-sm font-semibold text-gray-700">Row Breakdown</h4>
                                        </div>
                                        <div className="overflow-x-auto">
                                            <table className="table">
                                                <thead className="table-header">
                                                    <tr>
                                                        <th className="table-header-cell">Investor</th>
                                                        <th className="table-header-cell">LEI</th>
                                                        <th className="table-header-cell">Tokens</th>
                                                        <th className="table-header-cell">Wallet</th>
                                                        <th className="table-header-cell">Status</th>
                                                        <th className="table-header-cell">Processed</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="table-body">
                                                    {activeDistribution.rows?.length ? activeDistribution.rows.map((row) => {
                                                        const badge = statusBadgeConfig[row.status] || statusBadgeConfig.staged;
                                                        const Icon = badge.icon;
                                                        return (
                                                            <tr key={row._id} className="table-row">
                                                                <td className="table-cell">
                                                                    <div className="text-sm text-secondary-900">{row.investorName || row.investorEmail || row.investorId}</div>
                                                                    <div className="text-xs text-gray-500">ID: {row.investorId}</div>
                                                                </td>
                                                                <td className="table-cell text-sm">{row.lei}</td>
                                                                <td className="table-cell text-sm">{formatToken(row.tokenAmount)}</td>
                                                                <td className="table-cell font-mono text-xs text-gray-600">{row.walletAddress}</td>
                                                                <td className="table-cell">
                                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
                                                                        <Icon className="w-3.5 h-3.5 mr-1" />
                                                                        {badge.label}
                                                                    </span>
                                                                    {row.failureReason && (
                                                                        <div className="mt-1 text-xs text-red-500">{row.failureReason}</div>
                                                                    )}
                                                                </td>
                                                                <td className="table-cell text-xs text-gray-500">
                                                                    {row.processedAt ? formatDate(row.processedAt) : '—'}
                                                                </td>
                                                            </tr>
                                                        );
                                                    }) : (
                                                        <tr>
                                                            <td colSpan={6} className="table-cell py-6 text-sm text-center text-gray-500">
                                                                No rows recorded in this distribution.
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-sm text-gray-500">No distribution data available.</div>
                            )}
                        </div>
                        <div className="flex justify-end px-6 py-4 border-t">
                            <button className="btn-primary" onClick={() => setDetailsContext(null)}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Modal */}
            {confirmationModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
                    <div className="w-full max-w-md mx-4 bg-white rounded-lg shadow-xl">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-medium text-gray-900">
                                Confirm {confirmationModal.type?.includes('l1') ? 'L1' : 'L2'} Approval
                            </h3>
                        </div>
                        <div className="px-6 py-4">
                            <div className="mb-4">
                                <p className="mb-2 text-sm text-gray-600">
                                    <strong>Bond:</strong> {confirmationModal.bondName}
                                </p>
                                <p className="text-sm text-gray-700">
                                    {confirmationModal.message}
                                </p>
                            </div>
                            {confirmationModal.type === 'l2' && (
                                <div className="p-3 border border-orange-200 rounded-md bg-orange-50">
                                    <div className="flex items-start">
                                        <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5 mr-2 flex-shrink-0" />
                                        <div className="text-xs text-orange-800">
                                            <strong>Warning:</strong> This action will execute blockchain transfers and cannot be undone.
                                        </div>
                                    </div>
                                </div>
                            )}
                            {confirmationModal.type?.includes('l1') && (
                                <div className="p-3 border border-blue-200 rounded-md bg-blue-50">
                                    <div className="flex items-start">
                                        <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                                        <div className="text-xs text-blue-800">
                                            <strong>Note:</strong> After L1 approval, a different user must perform L2 approval to execute the transfers.
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="flex justify-end px-6 py-4 space-x-3 border-t border-gray-200">
                            <button
                                onClick={() => setConfirmationModal(null)}
                                className="btn-outline"
                                disabled={approveDistributionMutation.isLoading || approveManualAllocationMutation.isLoading}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    const { bondId, type, isManual } = confirmationModal;

                                    // CSV or Manual L1/L2 approval
                                    const level = type === 'l1' ? 'L1' : 'L2';
                                    if (isManual) {
                                        approveManualAllocationMutation.mutate({ bondId, level });
                                    } else {
                                        approveDistributionMutation.mutate({ bondId, level });
                                    }
                                    setConfirmationModal(null);
                                }}
                                className="btn-primary"
                                disabled={approveDistributionMutation.isLoading || approveManualAllocationMutation.isLoading}
                            >
                                {(approveDistributionMutation.isLoading || approveManualAllocationMutation.isLoading) ? 'Processing...' : 'Confirm'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default BondTokenAllocation;
